import { OptionType } from '@morph-mapper/types';
import type { Graph, NodeInstance } from '../../../types/graph';
import { CommandEffectKind, type CommandEffect } from '../types';

const collectGraphOptionIds = (node?: NodeInstance) => {
  const ids: string[] = [];
  const options = node?.options ?? {};

  Object.values(options).forEach((option) => {
    if (option.type !== OptionType.Graph) return;
    if (typeof option.graphId !== 'string') return;
    ids.push(option.graphId);
  });

  return ids;
};

export const deleteGraphsForNode = (node?: NodeInstance): CommandEffect[] => {
  return collectGraphOptionIds(node).map((graphId) => ({
    kind: CommandEffectKind.DeleteGraph,
    graphId,
  }));
};

export const restoreGraphsForNode = (node?: NodeInstance): CommandEffect[] => {
  return collectGraphOptionIds(node).map((graphId) => ({
    kind: CommandEffectKind.RestoreGraph,
    graphId,
  }));
};

export const restoreGraphsForAddedNodes = (
  before: Readonly<Graph>,
  after: Readonly<Graph>
): CommandEffect[] => {
  const beforeIds = new Set(Object.keys(before.nodes));
  const addedNodes = Object.values(after.nodes).filter(
    (node) => !beforeIds.has(node.id)
  );

  return addedNodes.flatMap((node) => restoreGraphsForNode(node));
};

export const deleteGraphsForRemovedNodes = (
  before: Readonly<Graph>,
  after: Readonly<Graph>
): CommandEffect[] => {
  const afterIds = new Set(Object.keys(after.nodes));
  const removedNodes = Object.values(before.nodes).filter(
    (node) => !afterIds.has(node.id)
  );

  return removedNodes.flatMap((node) => deleteGraphsForNode(node));
};

export const effectsForOptionChange = (
  beforeOption?: { type?: OptionType; graphId?: string },
  afterOption?: { type?: OptionType; graphId?: string }
): CommandEffect[] => {
  const effects: CommandEffect[] = [];

  if (beforeOption?.type === OptionType.Graph && beforeOption.graphId) {
    effects.push({
      kind: CommandEffectKind.DeleteGraph,
      graphId: beforeOption.graphId,
    });
  }

  if (afterOption?.type === OptionType.Graph && afterOption.graphId) {
    effects.push({
      kind: CommandEffectKind.RestoreGraph,
      graphId: afterOption.graphId,
    });
  }

  return effects;
};
